import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AudioButton } from "@/components/AudioButton";
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Save,
  X,
  Check,
  AlertCircle,
  Sparkles,
  Loader2,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getWords,
  updateTranslation,
  updateWordNotes,
  updateWordCategories,
  deleteWord,
  reanalyzeAndUpdateGrammar,
  updateWordSentences,
  GRAMMAR_SCHEMA_VERSION,
} from "@/hooks/useFirestore";
import { generateExampleSentences } from "@/hooks/useDirectApi";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { Word, SupportedLanguage, WordType, Gender, VerbInfo, PrepositionInfo } from "@/types";
import { calculateStrength, formatRelativeTime, cn } from "@/lib/utils";

export function WordDetailPage() {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { wordId } = useParams<{ wordId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: words, isLoading } = useQuery({
    queryKey: ["words", user?.uid],
    queryFn: () => getWords(user!.uid),
    enabled: !!user,
  });

  const word = words?.find((w) => w.id === wordId);

  const deleteMutation = useMutation({
    mutationFn: () => deleteWord(wordId!, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      navigate("/words");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!word) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Word not found</p>
        <Button variant="ghost" onClick={() => navigate("/words")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Word Bank
        </Button>
      </div>
    );
  }

  const strength = calculateStrength(
    word.reviewStats.knownCount,
    word.reviewStats.unknownCount
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/words")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={() => setShowDeleteDialog(true)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </Button>
      </div>

      {/* Word Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {/* Hebrew + Translation side by side */}
            <div className="flex items-center justify-between gap-4">
              <div className="text-3xl font-semibold">
                {word.translations[preferences.nativeLanguage]?.text || (
                  <span className="text-muted-foreground italic text-2xl">No translation</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <AudioButton text={word.hebrew.text} />
                <div className="text-4xl font-semibold hebrew-text" dir="rtl">
                  {word.hebrew.text}
                </div>
              </div>
            </div>

            {/* Transliteration */}
            {word.hebrew.transliteration && (
              <div className="text-center text-lg text-muted-foreground italic">
                ({word.hebrew.transliteration})
              </div>
            )}

            {/* Stats row */}
            <div className="flex items-center justify-center gap-2">
              <StrengthBadge strength={strength} />
              <span className="text-sm text-muted-foreground">
                Reviewed {word.reviewStats.knownCount + word.reviewStats.unknownCount} times
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Translations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TranslationEditor
            userId={user!.uid}
            wordId={word.id}
            language="en"
            translation={word.translations.en}
            label="English"
          />
          {/* TODO: Russian support hidden — re-enable by uncommenting */}
          {/* <TranslationEditor
            userId={user!.uid}
            wordId={word.id}
            language="ru"
            translation={word.translations.ru}
            label="Russian"
          /> */}
        </CardContent>
      </Card>

      {/* Grammar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Grammar</CardTitle>
        </CardHeader>
        <CardContent>
          <GrammarSection userId={user!.uid} word={word} />
        </CardContent>
      </Card>

      {/* Example Sentences */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Example Sentences</CardTitle>
        </CardHeader>
        <CardContent>
          <ExampleSentencesSection
            userId={user!.uid}
            word={word}
            nativeLanguage={preferences.nativeLanguage}
          />
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <CategoriesEditor userId={user!.uid} wordId={word.id} categories={word.categories} />
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <NotesEditor userId={user!.uid} wordId={word.id} notes={word.notes} />
        </CardContent>
      </Card>

      {/* Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Added</div>
              <div>{formatRelativeTime(word.firstAddedAt)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Last Lookup</div>
              <div>{formatRelativeTime(word.lastLookupAt)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Lookup Count</div>
              <div>{word.lookupCount}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Last Reviewed</div>
              <div>
                {word.lastReviewedAt
                  ? formatRelativeTime(word.lastReviewedAt)
                  : "Never"}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Known</div>
              <div className="text-green-600">{word.reviewStats.knownCount}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Unknown</div>
              <div className="text-red-600">{word.reviewStats.unknownCount}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Word</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{word.hebrew.text}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StrengthBadge({ strength }: { strength: "weak" | "learning" | "strong" }) {
  const labels = { weak: "Weak", learning: "Learning", strong: "Strong" };
  const variants = {
    weak: "bg-red-100 text-red-700",
    learning: "bg-amber-100 text-amber-700",
    strong: "bg-green-100 text-green-700",
  };

  return (
    <Badge className={cn("font-normal", variants[strength])}>
      {labels[strength]}
    </Badge>
  );
}

function TranslationEditor({
  userId,
  wordId,
  language,
  translation,
  label,
}: {
  userId: string;
  wordId: string;
  language: SupportedLanguage;
  translation?: { text: string; verified: boolean; source: string };
  label: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(translation?.text || "");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => updateTranslation(userId, wordId, language, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    if (value.trim()) {
      mutation.mutate();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground w-16">{label}</span>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1"
          autoFocus
        />
        <Button size="sm" onClick={handleSave} disabled={mutation.isPending}>
          <Save className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground w-16">{label}</span>
      <span className="flex-1">
        {translation?.text || <span className="text-muted-foreground italic">Not set</span>}
      </span>
      {translation?.verified && (
        <Badge variant="secondary" className="text-xs">
          <Check className="h-3 w-3 mr-1" />
          Verified
        </Badge>
      )}
      <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
        <Edit2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function CategoriesEditor({
  userId,
  wordId,
  categories,
}: {
  userId: string;
  wordId: string;
  categories: string[];
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [localCategories, setLocalCategories] = useState(categories);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => updateWordCategories(userId, wordId, localCategories),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      setIsEditing(false);
    },
  });

  const addCategory = () => {
    if (newCategory.trim() && !localCategories.includes(newCategory.trim())) {
      setLocalCategories([...localCategories, newCategory.trim()]);
      setNewCategory("");
    }
  };

  const removeCategory = (cat: string) => {
    setLocalCategories(localCategories.filter((c) => c !== cat));
  };

  if (isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {localCategories.map((cat) => (
            <Badge key={cat} variant="outline" className="cursor-pointer" onClick={() => removeCategory(cat)}>
              {cat}
              <X className="ml-1 h-3 w-3" />
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="Add category"
            onKeyDown={(e) => e.key === "Enter" && addCategory()}
          />
          <Button size="sm" onClick={addCategory}>Add</Button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => {
            setLocalCategories(categories);
            setIsEditing(false);
          }}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-wrap gap-2 flex-1">
        {categories.length > 0 ? (
          categories.map((cat) => (
            <Badge key={cat} variant="secondary">{cat}</Badge>
          ))
        ) : (
          <span className="text-muted-foreground italic">No categories</span>
        )}
      </div>
      <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
        <Edit2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function NotesEditor({ userId, wordId, notes }: { userId: string; wordId: string; notes: string }) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(notes);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => updateWordNotes(userId, wordId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      setIsEditing(false);
    },
  });

  if (isEditing) {
    return (
      <div className="space-y-2">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full min-h-[100px] p-2 border rounded-md text-sm"
          placeholder="Add notes..."
          autoFocus
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => {
            setValue(notes);
            setIsEditing(false);
          }}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        {notes || <span className="text-muted-foreground italic">No notes</span>}
      </div>
      <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
        <Edit2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function GrammarSection({ userId, word }: { userId: string; word: Word }) {
  const queryClient = useQueryClient();
  const { canUseFeature, openUpgradePrompt, isPro, grammarUsageRemaining, incrementGrammarUsage } = useSubscription();

  // Check if grammar schema is outdated
  const isSchemaOutdated = word.wordType && (word.grammarSchemaVersion || 1) < GRAMMAR_SCHEMA_VERSION;

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      // Use reanalyzeAndUpdateGrammar to update both global cache and user's word
      const grammar = await reanalyzeAndUpdateGrammar(userId, word.id, word.hebrew.text);
      return grammar;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      if (!isPro) {
        incrementGrammarUsage();
        // grammarUsageRemaining still has the pre-increment value here;
        // if it was 1, this was the last free analysis
        if (grammarUsageRemaining <= 1) {
          openUpgradePrompt("grammar");
        }
      }
    },
  });

  // Auto-update for Pro users when schema is outdated
  useEffect(() => {
    if (isPro && isSchemaOutdated && !analyzeMutation.isPending) {
      analyzeMutation.mutate();
    }
  }, [isPro, isSchemaOutdated]);

  const wordTypeLabels: Record<WordType, string> = {
    noun: "Noun (שם עצם)",
    verb: "Verb (פועל)",
    adjective: "Adjective (שם תואר)",
    adverb: "Adverb (תואר הפועל)",
    other: "Other",
  };

  const genderLabels: Record<Gender, string> = {
    masculine: "Masculine (זכר)",
    feminine: "Feminine (נקבה)",
    both: "Both (זכר/נקבה)",
  };

  const grammarLocked = !canUseFeature("grammar");

  // No grammar info yet
  if (!word.wordType) {
    return (
      <div className="text-center py-4">
        <p className="text-muted-foreground mb-3">No grammar information yet</p>
        {grammarLocked ? (
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openUpgradePrompt("grammar")}
            >
              <Lock className="mr-2 h-4 w-4" />
              Upgrade to Pro
            </Button>
            <p className="text-xs text-muted-foreground">
              Free grammar analyses used up this month
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => analyzeMutation.mutate()}
              disabled={analyzeMutation.isPending}
            >
              {analyzeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Analyze with AI
                </>
              )}
            </Button>
            {!isPro && (
              <p className="text-xs text-muted-foreground">
                {grammarUsageRemaining} free {grammarUsageRemaining === 1 ? "analysis" : "analyses"} remaining this month
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Word Type */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-muted-foreground">Word Type</div>
          <div className="font-medium">{wordTypeLabels[word.wordType]}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (grammarLocked) {
              openUpgradePrompt("grammar");
              return;
            }
            analyzeMutation.mutate();
          }}
          disabled={analyzeMutation.isPending}
          title={grammarLocked ? "Upgrade to Pro for unlimited grammar" : "Re-analyze with AI"}
        >
          {analyzeMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : grammarLocked ? (
            <Lock className="h-4 w-4" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Noun Info */}
      {word.wordType === "noun" && word.nounInfo && (
        <div className="space-y-3 pt-2 border-t overflow-x-auto">
          <div>
            <div className="text-sm text-muted-foreground">Gender</div>
            <div className="font-medium">{genderLabels[word.nounInfo.gender]}</div>
          </div>
          <table className="w-full border-collapse border" dir="rtl">
            <thead>
              <tr>
                <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">יחיד</th>
                <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">רבים</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-blue-50/50">
                <td className={cn("p-3 text-center hebrew-text text-lg font-medium")} dir="rtl">{word.nounInfo.singular ? <span className="inline-flex items-center gap-1">{word.nounInfo.singular}<AudioButton text={word.nounInfo.singular} size="sm" proOnly /></span> : "-"}</td>
                <td className={cn("p-3 text-center hebrew-text text-lg font-medium")} dir="rtl">{word.nounInfo.plural ? <span className="inline-flex items-center gap-1">{word.nounInfo.plural}<AudioButton text={word.nounInfo.plural} size="sm" proOnly /></span> : "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Adjective Info */}
      {word.wordType === "adjective" && word.adjectiveInfo && (
        <div className="space-y-3 pt-2 border-t overflow-x-auto">
          <table className="w-full border-collapse border min-w-[400px]" dir="rtl">
            <thead>
              <tr>
                <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">יחיד ז׳</th>
                <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">יחיד נ׳</th>
                <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">רבים ז׳</th>
                <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">רבים נ׳</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-blue-50/50">
                <td className={cn("p-3 text-center hebrew-text text-lg font-medium")} dir="rtl">{word.adjectiveInfo.singularMs ? <span className="inline-flex items-center gap-1">{word.adjectiveInfo.singularMs}<AudioButton text={`הוא ${word.adjectiveInfo.singularMs}`} size="sm" proOnly /></span> : "-"}</td>
                <td className={cn("p-3 text-center hebrew-text text-lg font-medium")} dir="rtl">{word.adjectiveInfo.singularFs ? <span className="inline-flex items-center gap-1">{word.adjectiveInfo.singularFs}<AudioButton text={`היא ${word.adjectiveInfo.singularFs}`} size="sm" proOnly /></span> : "-"}</td>
                <td className={cn("p-3 text-center hebrew-text text-lg font-medium")} dir="rtl">{word.adjectiveInfo.pluralMs ? <span className="inline-flex items-center gap-1">{word.adjectiveInfo.pluralMs}<AudioButton text={`הם ${word.adjectiveInfo.pluralMs}`} size="sm" proOnly /></span> : "-"}</td>
                <td className={cn("p-3 text-center hebrew-text text-lg font-medium")} dir="rtl">{word.adjectiveInfo.pluralFs ? <span className="inline-flex items-center gap-1">{word.adjectiveInfo.pluralFs}<AudioButton text={`הן ${word.adjectiveInfo.pluralFs}`} size="sm" proOnly /></span> : "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Verb Info */}
      {word.wordType === "verb" && word.verbInfo && (
        <VerbConjugationTable verbInfo={word.verbInfo} />
      )}

      {/* Verb Prepositions */}
      {word.wordType === "verb" && word.verbInfo && (
        <PrepositionsSection prepositions={word.verbInfo.prepositions} />
      )}

      {/* Adjective Preposition */}
      {word.wordType === "adjective" && word.adjectiveInfo && (
        <PrepositionsSection prepositions={word.adjectiveInfo.preposition ? [word.adjectiveInfo.preposition] : undefined} />
      )}

      {/* Update Grammar prompt for free users with outdated schema */}
      {!isPro && isSchemaOutdated && (
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3">
            <div className="text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-200">Grammar update available</p>
              <p className="text-amber-600 dark:text-amber-400 text-xs">New: prepositions & usage examples</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (grammarLocked) {
                  openUpgradePrompt("grammar");
                  return;
                }
                analyzeMutation.mutate();
              }}
              disabled={analyzeMutation.isPending}
            >
              {analyzeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : grammarLocked ? (
                <>
                  <Lock className="mr-1 h-3 w-3" />
                  Upgrade
                </>
              ) : (
                <>
                  <Sparkles className="mr-1 h-3 w-3" />
                  Update
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExampleSentencesSection({
  userId,
  word,
  nativeLanguage,
}: {
  userId: string;
  word: Word;
  nativeLanguage: string;
}) {
  const queryClient = useQueryClient();
  const { isPro, openUpgradePrompt } = useSubscription();
  const sentences = word.exampleSentences;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const translation = word.translations[nativeLanguage as SupportedLanguage]?.text
        || word.translations.en?.text
        || "";
      const examples = await generateExampleSentences(word.hebrew.text, translation, word.wordType);
      await updateWordSentences(userId, word.id, examples);
      return examples;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
    },
  });

  if (!sentences || sentences.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted-foreground mb-3">No example sentences yet</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => isPro ? generateMutation.mutate() : openUpgradePrompt("grammar")}
          disabled={generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : !isPro ? (
            <>
              <Lock className="mr-2 h-4 w-4" />
              Generate Examples
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Examples
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sentences.map((ex, i) => (
        <div key={i} className="space-y-1 pb-3 border-b last:border-b-0 last:pb-0">
          <div className="flex items-center gap-2 justify-end">
            <AudioButton text={ex.hebrew} size="sm" proOnly />
            <div className="hebrew-text text-lg font-medium" dir="rtl">{ex.hebrew}</div>
          </div>
          <div className="text-sm text-muted-foreground">{ex.english}</div>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="w-full"
        onClick={() => isPro ? generateMutation.mutate() : openUpgradePrompt("grammar")}
        disabled={generateMutation.isPending}
      >
        {generateMutation.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        Regenerate
      </Button>
    </div>
  );
}

function PrepositionsSection({ prepositions }: { prepositions?: PrepositionInfo[] }) {
  return (
    <div className="space-y-4 pt-4 border-t">
      <h4 className="font-medium text-sm text-muted-foreground">Common Prepositions</h4>
      {(!prepositions || prepositions.length === 0) ? (
        <p className="text-sm text-muted-foreground italic">No specific preposition required</p>
      ) : (
        prepositions.map((prep, idx) => (
          <div key={idx} className="space-y-2">
            <div className="text-right">
              <span className="text-2xl hebrew-text font-semibold">{prep.preposition}</span>
              <span className="text-muted-foreground ml-2">({prep.meaning})</span>
            </div>
            {prep.examples && prep.examples.length > 0 && (
              <div className="space-y-2 pr-4 border-r-2 border-muted ml-auto" style={{ width: 'fit-content' }}>
                {prep.examples.map((ex, i) => (
                  <div key={i} className="space-y-0.5 text-right">
                    <div className="hebrew-text text-base" dir="rtl">{ex.hebrew}</div>
                    <div className="text-sm text-muted-foreground italic">{ex.english}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function VerbConjugationTable({ verbInfo }: { verbInfo: VerbInfo }) {
  const HebrewCell = ({ text, className, pronoun }: { text?: string; className?: string; pronoun?: string }) => (
    <td className={cn("p-3 text-center hebrew-text text-lg font-medium", className)} dir="rtl">
      {text ? (
        <span className="inline-flex items-center gap-1">
          {text}
          <AudioButton text={pronoun ? `${pronoun} ${text}` : text} size="sm" proOnly />
        </span>
      ) : "-"}
    </td>
  );

  const HeaderCell = ({ children }: { children: React.ReactNode }) => (
    <th className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted/50">
      {children}
    </th>
  );

  const PronounCell = ({ children }: { children: React.ReactNode }) => (
    <td className="p-3 text-center hebrew-text text-base font-medium text-muted-foreground" dir="rtl">
      {children}
    </td>
  );

  return (
    <div className="space-y-6 pt-4 border-t">
      {/* Binyan & Infinitive - RTL layout */}
      <div className="flex justify-between items-start" dir="rtl">
        <div>
          <div className="text-sm text-muted-foreground">שם הפועל (Infinitive)</div>
          <div className="font-medium hebrew-text text-3xl">
            {verbInfo.infinitive}
          </div>
        </div>
        <div className="text-left">
          <div className="text-sm text-muted-foreground">בניין (Binyan)</div>
          <div className="font-bold hebrew-text text-3xl">
            {verbInfo.binyan}
          </div>
        </div>
      </div>

      {/* Present Tense Table */}
      <div className="overflow-x-auto">
        <div className="text-sm font-medium text-muted-foreground mb-2">הווה (Present)</div>
        <table className="w-full border-collapse border min-w-[400px]" dir="rtl">
          <thead>
            <tr>
              <HeaderCell>יחיד ז׳</HeaderCell>
              <HeaderCell>יחיד נ׳</HeaderCell>
              <HeaderCell>רבים ז׳</HeaderCell>
              <HeaderCell>רבים נ׳</HeaderCell>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-blue-50/50">
              <HebrewCell text={verbInfo.presentMs} pronoun="הוא" />
              <HebrewCell text={verbInfo.presentFs} pronoun="היא" />
              <HebrewCell text={verbInfo.presentMp} pronoun="הם" />
              <HebrewCell text={verbInfo.presentFp} pronoun="הן" />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Past & Future Tense Table */}
      <div className="overflow-x-auto">
        <div className="text-sm font-medium text-muted-foreground mb-2">עבר ועתיד (Past & Future)</div>
        <table className="w-full border-collapse border min-w-[400px]" dir="rtl">
          <thead>
            <tr>
              <HeaderCell>גוף</HeaderCell>
              <HeaderCell>עבר</HeaderCell>
              <HeaderCell>עתיד</HeaderCell>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <PronounCell>אני</PronounCell>
              <HebrewCell text={verbInfo.past1s} pronoun="אני" />
              <HebrewCell text={verbInfo.future1s} pronoun="אני" className="bg-green-50/50" />
            </tr>
            <tr className="border-b">
              <PronounCell>אתה</PronounCell>
              <HebrewCell text={verbInfo.past2ms} pronoun="אתה" />
              <HebrewCell text={verbInfo.future2ms} pronoun="אתה" className="bg-green-50/50" />
            </tr>
            <tr className="border-b">
              <PronounCell>את</PronounCell>
              <HebrewCell text={verbInfo.past2fs} pronoun="את" />
              <HebrewCell text={verbInfo.future2fs} pronoun="את" className="bg-green-50/50" />
            </tr>
            <tr className="border-b">
              <PronounCell>אנחנו</PronounCell>
              <HebrewCell text={verbInfo.past1p} pronoun="אנחנו" />
              <HebrewCell text={verbInfo.future1p} pronoun="אנחנו" className="bg-green-50/50" />
            </tr>
            <tr className="border-b">
              <PronounCell>הוא</PronounCell>
              <HebrewCell text={verbInfo.past3ms} pronoun="הוא" />
              <HebrewCell text={verbInfo.future3ms} pronoun="הוא" className="bg-green-50/50" />
            </tr>
            <tr className="border-b">
              <PronounCell>היא</PronounCell>
              <HebrewCell text={verbInfo.past3fs} pronoun="היא" />
              <HebrewCell text={verbInfo.future3fs} pronoun="היא" className="bg-green-50/50" />
            </tr>
            <tr className="border-b">
              <PronounCell>אתם/אתן</PronounCell>
              <HebrewCell text={verbInfo.past2mp && verbInfo.past2fp ? `${verbInfo.past2mp}/${verbInfo.past2fp.slice(-2)}` : verbInfo.past2mp} pronoun="אתם" />
              <HebrewCell text={verbInfo.future2mp} pronoun="אתם" className="bg-green-50/50" />
            </tr>
            <tr>
              <PronounCell>הם/הן</PronounCell>
              <HebrewCell text={verbInfo.past3p} pronoun="הם" />
              <HebrewCell text={verbInfo.future3mp} pronoun="הם" className="bg-green-50/50" />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Imperative Table */}
      <div className="overflow-x-auto">
        <div className="text-sm font-medium text-muted-foreground mb-2">ציווי (Imperative)</div>
        <table className="w-full border-collapse border min-w-[300px]" dir="rtl">
          <thead>
            <tr>
              <HeaderCell>יחיד ז׳</HeaderCell>
              <HeaderCell>יחיד נ׳</HeaderCell>
              <HeaderCell>רבים</HeaderCell>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-amber-50/50">
              <HebrewCell text={verbInfo.imperativeMs} />
              <HebrewCell text={verbInfo.imperativeFs} />
              <HebrewCell text={verbInfo.imperativeMp} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
